import { onDocumentReady } from '../utils/events';

const getVimeoEmbedUrl = (value) => {
  try {
    const url = new URL(value);
    if (
      !/^https?:$/.test(url.protocol) ||
      !/^(www\.)?vimeo\.com$|^player\.vimeo\.com$/.test(url.hostname)
    ) {
      return null;
    }
    const match = url.pathname.match(
      /^\/(?:video\/)?(\d+)(?:\/([a-zA-Z0-9]+))?\/?$/
    );
    if (!match) {
      return null;
    }
    const hash = url.searchParams.get('h') || match[2];
    return `https://player.vimeo.com/video/${match[1]}?autoplay=1${
      hash ? `&h=${encodeURIComponent(hash)}` : ''
    }`;
  } catch (error) {
    return null;
  }
};

const getYouTubeEmbedUrl = (value) => {
  try {
    const url = new URL(value);
    if (!/^https?:$/.test(url.protocol)) {
      return null;
    }

    let videoId = null;
    if (/^(www\.|m\.|music\.)?youtube\.com$/.test(url.hostname)) {
      if (url.pathname === '/watch') {
        videoId = url.searchParams.get('v');
      } else {
        const match = url.pathname.match(/^\/(?:embed|shorts|live)\/([^/?]+)/);
        videoId = match ? match[1] : null;
      }
    } else if (/^(www\.)?youtu\.be$/.test(url.hostname)) {
      videoId = url.pathname.split('/').filter(Boolean)[0] || null;
    }

    return /^[a-zA-Z0-9_-]{11}$/.test(videoId || '')
      ? `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`
      : null;
  } catch (error) {
    return null;
  }
};

onDocumentReady(() => {
  const videoLinks = document.querySelectorAll('[data-our-team-video]');

  if (!videoLinks.length) {
    return;
  }

  let modal = null;
  let activeLink = null;

  const closeModal = () => {
    if (!modal) {
      return;
    }

    const video = modal.querySelector('video');

    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }

    modal.remove();
    modal = null;
    document.body.classList.remove('is-our-team-video-open');

    if (activeLink) {
      activeLink.focus();
      activeLink = null;
    }
  };

  const handleKeydown = (event) => {
    if (!modal) {
      return;
    }
    if (event.key === 'Escape') {
      closeModal();
    }
    if (event.key === 'Tab') {
      const controls = modal.querySelectorAll('button, video, iframe');
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  const openModal = (link) => {
    const videoType = link.getAttribute('data-video-type');
    const isEmbeddedVideo = videoType === 'vimeo' || videoType === 'youtube';
    let videoUrl = link.getAttribute('href');

    if (videoType === 'vimeo') {
      videoUrl = getVimeoEmbedUrl(videoUrl);
    } else if (videoType === 'youtube') {
      videoUrl = getYouTubeEmbedUrl(videoUrl);
    }

    if (!videoUrl) {
      return false;
    }

    closeModal();
    activeLink = link;

    modal = document.createElement('div');
    modal.className = 'our-team-video-popup';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', link.getAttribute('aria-label'));

    const backdrop = document.createElement('div');
    backdrop.className = 'our-team-video-popup__backdrop';
    backdrop.setAttribute('data-our-team-video-close', '');

    const dialog = document.createElement('div');
    dialog.className = 'our-team-video-popup__dialog';

    const closeButton = document.createElement('button');
    closeButton.className = 'our-team-video-popup__close';
    closeButton.type = 'button';
    closeButton.setAttribute('data-our-team-video-close', '');
    closeButton.setAttribute(
      'aria-label',
      link.dataset.closeLabel || 'Close video'
    );

    const video = document.createElement(isEmbeddedVideo ? 'iframe' : 'video');
    video.className = 'our-team-video-popup__video';
    video.src = videoUrl;
    video.tabIndex = 0;
    if (isEmbeddedVideo) {
      video.title = link.getAttribute('aria-label');
      video.allow = 'autoplay; fullscreen; picture-in-picture';
      video.allowFullscreen = true;
    } else {
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
    }

    dialog.append(closeButton, video);
    modal.append(backdrop, dialog);

    document.body.appendChild(modal);
    document.body.classList.add('is-our-team-video-open');

    modal
      .querySelectorAll('[data-our-team-video-close]')
      .forEach((closeElement) => {
        closeElement.addEventListener('click', closeModal);
      });

    closeButton.focus();
    return true;
  };

  videoLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      if (openModal(link)) {
        event.preventDefault();
      }
    });
  });

  document.addEventListener('keydown', handleKeydown);
  document.addEventListener('focusin', (event) => {
    if (modal && !modal.contains(event.target)) {
      modal.querySelector('button').focus();
    }
  });
});
